import { useState, useCallback, useRef } from 'react'

const STORAGE_KEY = 'sk_progress'
const LEARNED_THRESHOLD = 0.8 // 80% listened = learned

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveProgress(progress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
}

/**
 * Track audio listening progress per lesson.
 * Progress is stored as: { [sectionId]: { maxPercent, learned, lastUpdated } }
 */
export function useProgress() {
  const [progress, setProgress] = useState(loadProgress)
  const pendingRef = useRef({}) // buffer updates to avoid excessive setState

  // Called frequently during audio playback — buffers updates
  const updateProgress = useCallback((sectionId, currentTime, duration) => {
    if (!sectionId || !duration || duration <= 0) return

    const percent = Math.min(currentTime / duration, 1)

    // Only update state when crossing 5% thresholds or reaching learned status
    const prev = pendingRef.current[sectionId] || progress[sectionId] || { maxPercent: 0, learned: false }
    const newMax = Math.max(prev.maxPercent, percent)

    // Skip if no meaningful change (less than 5% increase)
    if (newMax - prev.maxPercent < 0.05 && !(newMax >= LEARNED_THRESHOLD && !prev.learned)) {
      pendingRef.current[sectionId] = { ...prev, maxPercent: newMax }
      return
    }

    const entry = {
      maxPercent: newMax,
      learned: newMax >= LEARNED_THRESHOLD,
      lastUpdated: Date.now()
    }

    pendingRef.current[sectionId] = entry

    setProgress(prev => {
      const next = { ...prev, [sectionId]: entry }
      saveProgress(next)
      return next
    })
  }, [progress])

  // Flush any pending progress (call on pause/end)
  const flushProgress = useCallback((sectionId) => {
    const pending = pendingRef.current[sectionId]
    if (!pending) return

    setProgress(prev => {
      const existing = prev[sectionId] || { maxPercent: 0, learned: false }
      if (pending.maxPercent <= existing.maxPercent) return prev
      const entry = {
        maxPercent: pending.maxPercent,
        learned: pending.maxPercent >= LEARNED_THRESHOLD,
        lastUpdated: Date.now()
      }
      const next = { ...prev, [sectionId]: entry }
      saveProgress(next)
      return next
    })
  }, [])

  const getProgress = useCallback((sectionId) => {
    return progress[sectionId] || { maxPercent: 0, learned: false }
  }, [progress])

  const isLearned = useCallback((sectionId) => {
    return progress[sectionId]?.learned || false
  }, [progress])

  // Get stats for a list of section IDs
  const getCategoryStats = useCallback((sectionIds) => {
    let completed = 0
    let totalPercent = 0
    for (const id of sectionIds) {
      const p = progress[id]
      if (p) {
        if (p.learned) completed++
        totalPercent += Math.min(p.maxPercent, 1)
      }
    }
    return {
      total: sectionIds.length,
      completed,
      averagePercent: sectionIds.length > 0 ? totalPercent / sectionIds.length : 0
    }
  }, [progress])

  return { progress, updateProgress, flushProgress, getProgress, isLearned, getCategoryStats }
}
